import React, { useEffect, useState } from "react";

import { Button, Modal, Picto, Toggle } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import SubscriptionService from "@/services/SubscriptionService";

import { useAuth } from "@/hooks/useAuth";
import {
	goesThroughPortal,
	usePlans,
	useSubscription,
} from "@/hooks/useSubscription";

import { takePlanIntent } from "@/utils/UPlanIntent";
import { formatPrice, planFeatures } from "@/utils/USubscription";

import { PLAN_NAMES } from "@/constants/CPlans";
import { subscriptionKeys } from "@/constants/CQueryKeys";

import { TFeatureFlag } from "@/types";
import {
	PLAN_LIMIT_EVENT,
	TBillingInterval,
	TPlan,
	TPlanLimitDetails,
} from "@/types/TSubscription";

import { errorToast } from "@/modules/index";

/** Where to send the browser back once the plan is confirmed. */
export const RETURN_TO_KEY = "subscription:returnTo";

/** The sentence for a limit, with the plan's name and the cap it hit. */
export const planLimitMessage = (details: TPlanLimitDetails): string => {
	const plan = PLAN_NAMES[details.plan] ?? details.plan;
	if (details.limit === "clients" && details.max === 0) {
		return t("subscription.limit.clientsNone", { plan });
	}
	return t(`subscription.limit.${details.limit}`, {
		plan,
		max: details.max ?? 0,
		count: details.max ?? 0,
	});
};

/** The three things the required plan adds over the current one. */
export const upgradeBenefits = (
	required: TPlan,
	current: TPlan | undefined,
	on: Partial<Record<TFeatureFlag, boolean>> = {}
): string[] => {
	const have = new Set(current ? planFeatures(current, on) : []);
	return planFeatures(required, on)
		.filter((feature) => !have.has(feature))
		.slice(0, 3);
};

/**
 * The upgrade prompt: opens on the `plan:limit` event — a 402 from the
 * API, or a click on a locked button — with the plan that lifts the limit
 * pre-selected and a button straight to Checkout. Mounted once, in the
 * dashboard layout.
 */
export const UpgradeModal: React.FC = () => {
	const [details, setDetails] = useState<TPlanLimitDetails | null>(null);
	const [interval, setInterval] = useState<TBillingInterval>("month");
	const [busy, setBusy] = useState(false);
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { plan: current, subscription } = useSubscription();
	const { user } = useAuth();
	const plans = usePlans(!!details);

	useEffect(() => {
		const onLimit = (event: Event) => {
			setDetails((event as CustomEvent<TPlanLimitDetails>).detail);
			// The usage shown elsewhere is stale by definition now.
			void queryClient.invalidateQueries({
				queryKey: subscriptionKeys.current,
			});
		};
		window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
		return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
	}, [queryClient]);

	// The plan picked on the site before signing up: offered once the
	// dashboard is reached, i.e. once the onboarding is behind.
	useEffect(() => {
		const intent = takePlanIntent();
		if (intent) {
			setDetails({
				limit: "intent",
				plan: intent,
				max: null,
				used: 0,
				requiredPlan: intent,
			});
		}
	}, []);

	const close = () => setDetails(null);

	const seePlans = () => {
		close();
		navigate("/subscription");
	};

	const required =
		details?.requiredPlan && plans.data
			? plans.data.find((plan) => plan.code === details.requiredPlan)
			: undefined;
	const price =
		required &&
		(interval === "month" ? required.priceMonth : required.priceYear);
	const priceLabel =
		required && price !== null && price !== undefined
			? `${formatPrice(price, required.currency)} ${t(
					interval === "month"
						? "subscription.plans.perMonth"
						: "subscription.plans.perYear"
				)}`
			: null;

	const go = () => {
		if (!required) return;
		setBusy(true);
		try {
			sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname);
		} catch {
			// Private window: the success page falls back to home.
		}
		const prom = (
			goesThroughPortal(subscription)
				? SubscriptionService.portal()
				: SubscriptionService.checkout(required.code, interval)
		)
			.then((url) => {
				window.location.assign(url);
			})
			.finally(() => setBusy(false));
		// The busy button and the redirect say it all: a toast only on failure.
		errorToast(
			prom,
			t(
				goesThroughPortal(subscription)
					? "subscription.errors.portal"
					: "subscription.errors.checkout"
			)
		);
	};

	return (
		<Modal
			open={!!details}
			onClose={close}
			title={
				required
					? t("subscription.upgrade.title", { plan: required.name })
					: t("subscription.limit.title")
			}
			size="sm"
		>
			{details && (
				<div className="flex flex-col gap-5">
					<p
						className="rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-700"
						data-testid="plan-limit-message"
					>
						{planLimitMessage(details)}
					</p>

					{required ? (
						<>
							<ul className="flex flex-col gap-2 text-sm">
								{upgradeBenefits(
									required,
									current,
									user?.features
								).map((benefit) => (
									<li
										key={benefit}
										className="flex items-start gap-2"
									>
										<Picto
											icon="check"
											currentColor
											className="mt-0.5 h-4 w-4 shrink-0 text-green-600"
										/>
										<span>{benefit}</span>
									</li>
								))}
							</ul>

							{!goesThroughPortal(subscription) && (
								<div className="flex items-center gap-3 text-sm">
									<span>
										{t("subscription.plans.monthly")}
									</span>
									<Toggle
										checked={interval === "year"}
										onChange={(checked: boolean) =>
											setInterval(
												checked ? "year" : "month"
											)
										}
										aria-label={t(
											"subscription.plans.yearly"
										)}
									/>
									<span>
										{t("subscription.plans.yearly")}
									</span>
								</div>
							)}

							<p
								className="text-2xl font-semibold tabular-nums"
								data-testid="upgrade-price"
							>
								{priceLabel ??
									t("subscription.plans.priceUnset")}
							</p>

							<Button
								color="primary"
								isLoading={busy}
								onClick={go}
								data-testid="upgrade-cta"
							>
								{goesThroughPortal(subscription)
									? t("subscription.upgrade.ctaPortal")
									: t("subscription.upgrade.cta", {
											plan: required.name,
										})}
							</Button>
						</>
					) : (
						<p className="text-sm text-neutral-500">
							{details.requiredPlan
								? t("subscription.limit.required", {
										plan:
											PLAN_NAMES[details.requiredPlan] ??
											details.requiredPlan,
									})
								: t("subscription.upgrade.noPlan")}
						</p>
					)}

					<div className="flex justify-between gap-2">
						<Button variant="ghost" color="neutral" onClick={close}>
							{t("subscription.limit.later")}
						</Button>
						<Button variant="link" onClick={seePlans}>
							{t("subscription.upgrade.compare")}
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
};
